import { action, observable } from 'mobx';
import {
	Model,
	IModelAttributes,
	attribute,
	entity,
	ReferencePath,
} from 'Models/Model';
import * as Models from 'Models/Entities';
import * as Validators from 'Validators';
import { CRUD } from '../CRUDOptions';
import * as AttrUtils from 'Util/AttributeUtils';
import { IAcl } from 'Models/Security/IAcl';
import {
	makeFetchOneToManyFunc,
	getCreatedModifiedCrudOptions,
} from 'Util/EntityUtils';
import UserServicePrice from 'Models/Security/Acl/UserServicePrice';
import VisitorsServicePrice from 'Models/Security/Acl/VisitorsServicePrice';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';

export const MvsServicePriceCodes = {
	Basic: "MVS_Basic",
	Ppsr: "MVS_PPSRCERT",
	Comprehensive: "MVS_COMP"
}
export type MvsServicePriceCode = typeof MvsServicePriceCodes[keyof typeof MvsServicePriceCodes];

export interface IServicePriceEntityAttributes extends IModelAttributes {
	code: string;
	name: string;
	cost: number;

	searchRequests: Array<
		| Models.SearchRequestEntity
		| Models.ISearchRequestEntityAttributes
	>;

	popular: boolean;
	enabled: boolean;
	featureDescription: string;
}

@entity('ServicePriceEntity', 'Service Price')
export default class ServicePriceEntity extends Model
	implements IServicePriceEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new UserServicePrice(),
		new VisitorsServicePrice(),
	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@Validators.Unique()
	@observable
	@attribute<ServicePriceEntity, string>()
	@CRUD({
		name: 'Code',
		displayType: 'textfield',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public code: string;

	@observable
	@attribute<ServicePriceEntity, string>()
	@CRUD({
		name: 'Name',
		displayType: 'textfield',
		order: 20,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public name: string;

	@Validators.Numeric()
	@observable
	@attribute<ServicePriceEntity, number>()
	@CRUD({
		name: 'Cost',
		displayType: 'textfield',
		order: 30,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseFloat,
	})
	public cost: number;

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({

		name: 'Search Requests',
		displayType: 'reference-multicombobox',
		order: 40,
		referenceTypeFunc: () => Models.SearchRequestEntity,
		disableDefaultOptionRemoval: true,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'searchRequests',
			oppositeEntity: () => Models.SearchRequestEntity,
		}),

	})
	public searchRequests: Models.SearchRequestEntity[] = [];


	@observable
	@attribute<ServicePriceEntity, string>()
	@CRUD({
		name: 'Feature Description',
		displayType: 'textfield',
		order: 20,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public featureDescription: string;

	@Validators.Required()
	@observable
	@attribute<ServicePriceEntity, boolean>()
	@CRUD({
		name: 'Popular',
		displayType: 'checkbox',
		order: 100,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseBoolean,
		displayFunction: attr => attr ? 'True' : 'False',
	})
	public popular: boolean = false;

	@Validators.Required()
	@observable
	@attribute<ServicePriceEntity, boolean>()
	@CRUD({
		name: 'Enabled',
		displayType: 'checkbox',
		order: 100,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseBoolean,
		displayFunction: attr => attr ? 'True' : 'False',
	})
	public enabled: boolean = false;

	constructor(attributes?: Partial<IServicePriceEntityAttributes>) {
		super(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IServicePriceEntityAttributes>) {

		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.code !== undefined) {
				this.code = attributes.code;
			}
			if (attributes.name !== undefined) {
				this.name = attributes.name;
			}
			if (attributes.cost !== undefined) {
				this.cost = attributes.cost;
			}
			if (attributes.searchRequests !== undefined && Array.isArray(attributes.searchRequests)) {
				for (const model of attributes.searchRequests) {
					if (model instanceof Models.SearchRequestEntity) {
						this.searchRequests.push(model);
					} else {
						this.searchRequests.push(new Models.SearchRequestEntity(model));
					}
				}
			}

			if (attributes.featureDescription !== undefined) {
				this.featureDescription = attributes.featureDescription;
			}

			if (attributes.popular !== undefined) {
				this.popular = attributes.popular;
			}

			if (attributes.enabled !== undefined) {
				this.enabled = attributes.enabled;
			}

		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	public defaultExpands = `
		searchRequests {
			${Models.SearchRequestEntity.getAttributes().join('\n')}
			${Models.SearchRequestEntity.getFiles().map(f => f.name).join('\n')}
		}
	`;

	/**
	 * The save method that is called from the admin CRUD components.
	 */

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
			searchRequests: {},
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
						],
					},
				],
			},
		);
	}

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		return this.id;
	}
}

/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(ServicePriceEntity.prototype, 'created');
CRUD(modifiedAttr)(ServicePriceEntity.prototype, 'modified');